import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";

const GENRES = [
  "Memoir / Personal Narrative",
  "Social Justice Nonfiction",
  "Fiction",
  "Poetry",
  "Essay Collection",
  "Young Adult",
  "Other",
];

const INITIAL_FORM = {
  author_name: "", email: "", phone: "", title: "", genre: "",
  word_count: "", synopsis: "", sample_chapters: "", bio: "",
};

const MAX_FILE_MB = 25;
const ACCEPTED_EXT = [".pdf", ".docx"];
const ACCEPTED_MIME = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export default function Submit() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFileChange = (e) => {
    setFileError("");
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    const nameLower = f.name.toLowerCase();
    const okExt = ACCEPTED_EXT.some((ext) => nameLower.endsWith(ext));
    const okMime = ACCEPTED_MIME.includes(f.type);
    if (!okExt && !okMime) {
      setFile(null);
      setFileError("Only PDF or DOCX files are accepted.");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      setFile(null);
      setFileError(`File must be under ${MAX_FILE_MB} MB.`);
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("author_name", form.author_name);
      fd.append("email", form.email);
      fd.append("title", form.title);
      fd.append("genre", form.genre);
      fd.append("synopsis", form.synopsis);
      fd.append("sample_chapters", form.sample_chapters || "");
      if (form.phone) fd.append("phone", form.phone);
      if (form.word_count) fd.append("word_count", String(parseInt(form.word_count)));
      if (form.bio) fd.append("bio", form.bio);
      if (file) fd.append("file", file);

      await api.post("/manuscripts", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success("Manuscript received. Check your email.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (done) return <SuccessScreen />;

  return (
    <div className="bg-white" data-testid="submit-page">
      <Seo
        title="Submit Your Manuscript | 1123 Publishing Company"
        description="Submit your manuscript to 1123 Publishing. We acquire memoirs, social justice nonfiction, fiction, poetry, and transformational literature from debut and established authors."
        path="/submit"
      />
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <SubmitHeader />
        <FocusBlock />
        <ExpectationsBlock />
        <HowToBlock />
        <ManuscriptForm
          form={form} update={update}
          file={file} onFileChange={onFileChange} fileError={fileError}
          loading={loading} onSubmit={onSubmit}
        />
      </section>
    </div>
  );
}

function SuccessScreen() {
  return (
    <div className="py-32 bg-white" data-testid="submit-success">
      <div className="max-w-2xl mx-auto px-6 text-center">
        <div className="overline mb-4">Submission Received</div>
        <h1 className="font-serif text-5xl text-navy">Thank you.</h1>
        <p className="mt-6 text-charcoal/80 leading-relaxed">
          Our editorial team reviews every submission with care. You can expect to hear from us within
          6&ndash;8 weeks at the email you provided.
        </p>
        <div className="ornament-rule mt-12 max-w-xs mx-auto"><span>&#10086;</span></div>
      </div>
    </div>
  );
}

function SubmitHeader() {
  return (
    <div className="text-center mb-12">
      <div className="overline">Submissions</div>
      <h1 className="font-serif text-5xl md:text-6xl text-navy mt-3 leading-tight">
        We&rsquo;re Looking for Your Next Book.
      </h1>
      <p className="mt-6 text-charcoal/80 leading-relaxed">
        1123 Publishing exists to put memoirs and social-justice stories into the world &mdash;
        particularly from authors whose lives and lineage have been left out of the record.
        We also publish fiction, poetry, essay collections, and YA that share that same spirit.
        If that&rsquo;s you, we want to read your work.
      </p>
    </div>
  );
}

function FocusBlock() {
  return (
    <div className="bg-parchment border border-navy/15 p-6 md:p-10 mb-10 text-sm leading-relaxed grid md:grid-cols-2 gap-8">
      <div>
        <div className="overline mb-3">Primary Focus</div>
        <ul className="space-y-1 text-charcoal/85">
          <li>&#10086; Memoir &amp; personal narrative</li>
          <li>&#10086; Social-justice nonfiction</li>
          <li>&#10086; Stories from justice-impacted authors</li>
        </ul>
      </div>
      <div>
        <div className="overline mb-3">Also Welcome</div>
        <ul className="space-y-1 text-charcoal/85">
          <li>&#10086; Fiction</li>
          <li>&#10086; Poetry</li>
          <li>&#10086; Essay collections &amp; nonfiction</li>
          <li>&#10086; Young adult</li>
        </ul>
      </div>
    </div>
  );
}

function ExpectationsBlock() {
  return (
    <div className="grid md:grid-cols-2 gap-6 mb-10 text-sm leading-relaxed">
      <div className="border border-navy/15 p-6">
        <div className="overline mb-3">What We&rsquo;re Looking For</div>
        <ul className="space-y-2 text-charcoal/85">
          <li>&#10086; A finished or near-finished manuscript</li>
          <li>&#10086; A clear, distinct voice</li>
          <li>&#10086; A story that hasn&rsquo;t been told this way before</li>
        </ul>
      </div>
      <div className="border border-navy/15 p-6">
        <div className="overline mb-3">What You Get</div>
        <ul className="space-y-2 text-charcoal/85">
          <li>&#10086; Editorial support</li>
          <li>&#10086; Cover design &amp; formatting</li>
          <li>&#10086; Distribution to major retailers</li>
          <li>&#10086; Author marketing support</li>
        </ul>
      </div>
    </div>
  );
}

function HowToBlock() {
  return (
    <div className="bg-parchment border border-navy/15 p-6 md:p-10 mb-10 text-sm leading-relaxed">
      <div className="overline mb-3">How to Submit</div>
      <ol className="list-decimal list-inside space-y-1 text-charcoal/85">
        <li>Complete the form below with your synopsis and sample chapters.</li>
        <li>Attach a PDF or DOCX of the full manuscript if you have one (optional, up to 25 MB).</li>
        <li>Include a short author bio.</li>
        <li>We respond within <strong>6&ndash;8 weeks</strong>.</li>
      </ol>
    </div>
  );
}

function ManuscriptForm({ form, update, file, onFileChange, fileError, loading, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="space-y-6" data-testid="manuscript-form">
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Author Name" required>
          <input required value={form.author_name} onChange={update("author_name")} data-testid="ms-author-name"
            className="w-full bg-white border border-navy/20 px-4 py-3 focus:border-gold focus:outline-none" />
        </Field>
        <Field label="Email" required>
          <input required type="email" value={form.email} onChange={update("email")} data-testid="ms-email"
            className="w-full bg-white border border-navy/20 px-4 py-3 focus:border-gold focus:outline-none" />
        </Field>
        <Field label="Phone (optional)">
          <input value={form.phone} onChange={update("phone")} data-testid="ms-phone"
            className="w-full bg-white border border-navy/20 px-4 py-3 focus:border-gold focus:outline-none" />
        </Field>
        <Field label="Word Count (estimate)">
          <input type="number" value={form.word_count} onChange={update("word_count")} data-testid="ms-words"
            className="w-full bg-white border border-navy/20 px-4 py-3 focus:border-gold focus:outline-none" />
        </Field>
      </div>

      <Field label="Manuscript Title" required>
        <input required value={form.title} onChange={update("title")} data-testid="ms-title"
          className="w-full bg-white border border-navy/20 px-4 py-3 focus:border-gold focus:outline-none" />
      </Field>

      <Field label="Genre" required>
        <select required value={form.genre} onChange={update("genre")} data-testid="ms-genre"
          className="w-full bg-white border border-navy/20 px-4 py-3 focus:border-gold focus:outline-none">
          <option value="">Select a genre&hellip;</option>
          {GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
      </Field>

      <Field label="Synopsis" required>
        <textarea required rows={6} value={form.synopsis} onChange={update("synopsis")} data-testid="ms-synopsis"
          placeholder="300–500 words describing the manuscript, its themes, and its audience."
          className="w-full bg-white border border-navy/20 px-4 py-3 focus:border-gold focus:outline-none" />
      </Field>

      <Field label="Full Manuscript File (optional)">
        <FileDropzone file={file} onChange={onFileChange} error={fileError} />
      </Field>

      <Field label="Sample Chapters (paste text — optional if you attach a file)">
        <textarea rows={10} value={form.sample_chapters} onChange={update("sample_chapters")} data-testid="ms-sample"
          placeholder="If you didn't attach a file above, paste at least two chapters or thirty pages here."
          className="w-full bg-white border border-navy/20 px-4 py-3 focus:border-gold focus:outline-none font-serif" />
      </Field>

      <Field label="Author Bio (short)">
        <textarea rows={4} value={form.bio} onChange={update("bio")} data-testid="ms-bio"
          className="w-full bg-white border border-navy/20 px-4 py-3 focus:border-gold focus:outline-none" />
      </Field>

      <button type="submit" disabled={loading} data-testid="ms-submit"
        className="w-full bg-navy text-ivory py-4 text-[11px] uppercase tracking-[0.24em] hover:bg-charcoal disabled:opacity-60 transition-colors">
        {loading ? "Submitting..." : "Submit Your Manuscript"}
      </button>
    </form>
  );
}

function FileDropzone({ file, onChange, error }) {
  return (
    <div>
      <label
        htmlFor="ms-file"
        className="flex flex-col items-center justify-center gap-2 border border-dashed border-navy/30 bg-parchment/60 px-6 py-8 cursor-pointer hover:border-gold hover:bg-parchment transition-colors"
        data-testid="ms-file-dropzone"
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-navy/60">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9m0 0l-3 3m3-3l3 3M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z" />
        </svg>
        <div className="text-sm text-navy font-serif">
          {file ? file.name : "Click to attach PDF or DOCX"}
        </div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-navy/50">
          {file ? `${(file.size / (1024 * 1024)).toFixed(1)} MB` : "Max 25 MB — optional"}
        </div>
      </label>
      <input
        id="ms-file"
        type="file"
        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={onChange}
        className="sr-only"
        data-testid="ms-file-input"
      />
      {error && <div className="mt-2 text-xs text-red-700" data-testid="ms-file-error">{error}</div>}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="overline">{label}{required && <span className="text-gold ml-1">*</span>}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
